<template>
  <NuxtLayout name="standard">
    <ConnectifTag :name="CONNECTIF_PAGE_TAGS.HOME" />
    <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.HOME" />
    <template v-if="strapiPageData">
      <SHeroSearch
        :image="strapiPageData.attributes.hero.image.data.attributes.url"
        :title="t('landings.home.hero.title')"
        :badge="strapiPageData.attributes.hero.author"
      ></SHeroSearch>
      <SSection
        v-if="strapiPageData.attributes.simple_banner"
        :has-separator="false"
      >
        <SSimpleBanner
          :text="strapiPageData.attributes.simple_banner.text"
          :button="strapiPageData.attributes.simple_banner.button"
          @banner-click="simpleBannerClick"
        ></SSimpleBanner>
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.intro_seo_text"
        :title="strapiPageData.attributes.intro_seo_text.title"
        :has-separator="false"
        :full-width="false"
        :centered="true"
      >
        <SShortDescription
          :text="strapiPageData.attributes.intro_seo_text.text"
        />
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.image_banner"
        :has-separator="false"
        :mobile-full="true"
      >
        <SCtaCard v-bind="imageBanner" @button-click="imageBannerClick" />
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.featured_destinations"
        :title="t('landings.home.featured-destinations.title')"
        :centered="true"
        :has-separator="false"
      >
        <template #no-container>
          <SCardsImages
            v-bind="featuredDestinations"
            @button-click="cardsImagesButtonClick"
          />
        </template>
      </SSection>
      <SSection
        v-if="strapiPageData.attributes.media_banner"
        :has-separator="false"
      >
        <SMediaBanner
          v-if="mediaBanner"
          v-bind="mediaBanner"
          @media-banner-click="mediaBannerClick"
        />
      </SSection>
      <LazySSection
        v-if="bestCottages?.items.length > 0"
        :title="t('landings.home.best-cottages.title')"
        :has-separator="false"
      >
        <template #no-container>
          <SCardsRated :items="bestCottages.items" />
          <TContainer>
            <UButton
              :ui="mobileFullWidthButton"
              size="md"
              color="neutral"
              variant="subtle"
              :label="t('landings.home.best-cottages.button')"
              :to="routes().cottageList()"
              external
              class="mt-7"
            />
          </TContainer>
        </template>
      </LazySSection>
      <LazySSection
        v-if="deals?.items.length > 0"
        :title="t('landings.home.deals.title')"
        :has-separator="false"
      >
        <template #no-container>
          <SCardsDeals :items="deals.items" />
          <TContainer>
            <UButton
              :ui="mobileFullWidthButton"
              size="md"
              color="neutral"
              variant="subtle"
              :label="t('landings.home.deals.button')"
              :to="routes().dealsList()"
              external
              class="mt-7"
            />
          </TContainer>
        </template>
      </LazySSection>
      <LazySSection
        v-if="strapiPageData.attributes.featured_searches"
        :title="t('landings.home.featured-searches.title')"
        :has-separator="false"
      >
        <template #no-container>
          <SCardsTinyImages v-bind="featuredSearches" />
        </template>
      </LazySSection>

      <LazySCardsRatedWrapper :items="featuredCottages" />
      <LazySSectionColor
        v-if="strapiPageData.attributes.featured_posts"
        :title="strapiPageData.attributes.featured_posts.title"
        background-color="gray-50"
      >
        <SBlogposts v-if="dataPosts" v-bind="dataPosts" />
      </LazySSectionColor>
      <LazySSection
        v-if="reviews && reviews.items.length > 0"
        :title="t('landings.home.reviews.title')"
        :has-separator="false"
      >
        <SReviews v-bind="reviews" />
      </LazySSection>
      <LazySSection
        v-if="provinceLinks.length > 0"
        :title="t('landings.home.seoLinks.provinces.title')"
      >
        <SLinks :items="provinceLinks"></SLinks>
      </LazySSection>
      <LazySSection
        v-if="regionLinks.length > 0"
        :title="t('landings.home.seoLinks.regions.title')"
      >
        <SLinks :items="regionLinks"></SLinks>
      </LazySSection>
      <LazySSection
        v-if="villageLinks.length > 0"
        :title="t('landings.home.seoLinks.villages.title')"
      >
        <SLinks :items="villageLinks"></SLinks>
      </LazySSection>
      <SSpacer />
    </template>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import { MenuPublicHome } from '~~/modules/menu/constants/public-menu'

// Types
import { type schemaType as contactPageSchematype } from '~~/shared/api/strapi/home/schema'
import type { propsInterface as SLinksPropsInterface } from '~~/modules/design-system/sections/s-links/s-links.vue'

// Composables
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useHeadSchemaComposable } from '~~/modules/head/composables/useHeadSchemaComposable'

// Constants
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'
import {
  sendHomeSimpleBannerClick,
  sendHomeMediaBannerClick,
  sendHomeFeaturedDestinationsClick,
  sendHomeImageBannerClick,
} from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Transformers
import { useToSMediaBanner } from '~/composables/transformers/useToSMediaBanner'
import { useToSCardsRated } from '~/composables/transformers/useToSCardsRated'
import { useToSCardsDeals } from '~/composables/transformers/useToSCardsDeals'
import { useToSCardsImages } from '~/composables/transformers/useToSCardsImages'
import { useToSCardsTinyImages } from '~/composables/transformers/useToSCardsTinyImages'
import { useToSBlogposts } from '~/composables/transformers/useToSBlogposts'
import { useToSCtaCard } from '~/composables/transformers/useToSCtaCard'
import { useToSReviews } from '~/composables/transformers/useToSReviews'
import { useToSCardsRatedWrapper } from '~/composables/transformers/useToSCardsRatedWrapper'
import { useToSLinks } from '~/composables/transformers/useToSLinks'

// Components Props
import type { propsInterface as sMediaBannerProps } from '~~/modules/design-system/sections/s-media-banner/s-media-banner.vue'
import type { propsInterface as sCardsRatedProps } from '~~/modules/design-system/sections/s-cards-rated/index.vue'
import type { propsInterface as sCardsDealsProps } from '~~/modules/design-system/sections/s-cards-deals.vue'
import type { propsInterface as sCardsImagesProps } from '~~/modules/design-system/sections/s-cards-images/s-cards-images.vue'
import type { propsInterface as sCardsTinyImagesProps } from '~~/modules/design-system/sections/s-cards-tiny-images/s-cards-tiny-images.vue'
import type { propsInterface as SBlogPostsProps } from '~~/modules/design-system/sections/s-blogposts.vue'
import type { propsInterface as sCtaCardProps } from '~~/modules/design-system/sections/s-cta-card/s-cta-card.vue'
import type { propsInterface as sReviewsProps } from '~~/modules/design-system/sections/s-reviews.vue'
import type { propsInterface as sCardsRatedWrapper } from '~~/modules/design-system/sections/s-cards-rated/s-cards-rated-wrapper.vue'

// Apis
import { serverFetch as getPageData } from '~~/modules/api/strapi/home'
import { type returnType as pageDataType } from '~~/shared/api/strapi/home/schema'
import { serverFetch as getCottagesHomeBest } from '~~/modules/api/rurality/v3/cottages/home/best'
import { serverFetch as getDealsHome } from '~~/modules/api/rurality/v3/deals/home'
import { serverFetch as getBlogPosts } from '~~/modules/api/rurality/v3/blog/posts/list'
import { serverFetch as getCottagesReviewsHome } from '~~/modules/api/rurality/v3/cottage-reviews/home'
import { serverFetch as getProvinces } from '~~/modules/api/rurality/v3/provinces/detail'
import { type returnType as provincesReturnType } from '~~/shared/api/rurality/v3/provinces/detail/schema'
import { serverFetch as getRegions } from '~~/modules/api/rurality/v3/regions/list'
import { type returnType as regionsReturnType } from '~~/shared/api/rurality/v3/regions/list/schema'
import { serverFetch as getVillages } from '~~/modules/api/rurality/v3/villages/home'
import { type returnType as villagesReturnType } from '~~/shared/api/rurality/v3/villages/home/schema'
import { serverFetch as getCottagesList } from '~~/modules/api/rurality/v3/cottages/list'

// Components UI
import { mobileFullWidthButton } from '~~/modules/design-system/utils/nuxt-ui-config/components/button'

definePageMeta({
  publicMenuItem: MenuPublicHome,
})

// Composables

const { setHeadInfo } = useHeadInfoComposable()
const { organizationSchema, websiteSchema } = useHeadSchemaComposable()

// Methods
const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error.message,
    fatal: true,
  })
}

const [rawStrapiPageData, rawRegionsData, rawProvincesData, rawVillagesData]: [
  pageDataType,
  regionsReturnType,
  provincesReturnType,
  villagesReturnType,
] = await Promise.all([
  getPageData(),
  getRegions(),
  getProvinces(),
  getVillages(),
])

let strapiPageData: contactPageSchematype
let imageBanner: sCtaCardProps
let featuredDestinations: sCardsImagesProps
let mediaBanner: sMediaBannerProps | null
let bestCottages: sCardsRatedProps
let deals: sCardsDealsProps
let featuredSearches: sCardsTinyImagesProps
const featuredCottages: sCardsRatedWrapper['items'] = []
let dataPosts: SBlogPostsProps
let reviews: sReviewsProps
let provinceLinks: SLinksPropsInterface['items'] = []
let regionLinks: SLinksPropsInterface['items'] = []
let villageLinks: SLinksPropsInterface['items'] = []

if (rawStrapiPageData.success && rawStrapiPageData.success.data) {
  strapiPageData = rawStrapiPageData.success.data

  if (strapiPageData) {
    setHeadInfo({
      title: t('landings.home.seo.title'),
      description: t('landings.home.seo.description'),
      images: [
        {
          url: strapiPageData.attributes.hero.image.data.attributes.url,
          alt: t('landings.home.seo.image-alt'),
        },
      ],
      robotsVisible: true,
    })

    if (strapiPageData.attributes.image_banner) {
      const imageBannerData = useToSCtaCard().fromStrapiHome(strapiPageData)
      if (imageBannerData) {
        imageBanner = imageBannerData
      }
    }

    if (strapiPageData.attributes.featured_destinations) {
      featuredDestinations = useToSCardsImages().fromStrapiHome(strapiPageData)
    }

    if (strapiPageData.attributes.media_banner) {
      mediaBanner = useToSMediaBanner().fromStrapiHome(strapiPageData)
    }

    const cottagesHomeBest = await getCottagesHomeBest()
    if (cottagesHomeBest.success) {
      bestCottages = useToSCardsRated().fromCottagesHomeBest(
        cottagesHomeBest.success.data,
      )
    }

    const dealsHome = await getDealsHome({
      limit: 4,
    })
    if (dealsHome.success) {
      deals = useToSCardsDeals().fromDealsHome(dealsHome.success.data)
    }

    if (strapiPageData.attributes.featured_searches) {
      featuredSearches = useToSCardsTinyImages().fromStrapiHome(strapiPageData)
    }

    if (strapiPageData.attributes.featured_posts) {
      const getPosts = await getBlogPosts({
        slugs: strapiPageData.attributes.featured_posts.slugs,
      })
      if (getPosts.success && getPosts.success.data) {
        dataPosts = useToSBlogposts().fromBlogPostList(getPosts.success.data)
        if (strapiPageData.attributes.featured_posts.button) {
          dataPosts.button = strapiPageData.attributes.featured_posts.button
        }
      }
    }

    const cottagesReviewsHome = await getCottagesReviewsHome({
      limit: 6,
    })
    if (cottagesReviewsHome.success) {
      reviews = useToSReviews().fromRuralityCottageReviewsHome(
        cottagesReviewsHome.success.data,
      )
      reviews.variant = 'no-review-link'
    }
    if (strapiPageData.attributes.featured_cottages) {
      for (
        let i = 0;
        i < strapiPageData.attributes.featured_cottages.length;
        i++
      ) {
        const cottage = strapiPageData.attributes.featured_cottages[i]
        const facets = cottage.filters

        const dataGetCottages = await getCottagesList({ facets })
        if (dataGetCottages?.success) {
          const featuredCottage: sCardsRatedWrapper['items'][number] =
            useToSCardsRatedWrapper().fromStrapiHome(
              cottage,
              dataGetCottages.success.data,
            )
          featuredCottages.push(featuredCottage)
        }
      }
    }

    organizationSchema()
    websiteSchema()
    sendConnectifPage()
  }
} else if (rawStrapiPageData.error) {
  showErrorPage(rawStrapiPageData.error as ApiResponseError)
}

const { fromHomeLinks } = useToSLinks()

if (rawRegionsData.success && rawRegionsData.success.data) {
  regionLinks = fromHomeLinks(rawRegionsData.success.data.regions)
}

if (rawProvincesData.success && rawProvincesData.success.data) {
  provinceLinks = fromHomeLinks(rawProvincesData.success.data.provinces)
}

if (rawVillagesData.success && rawVillagesData.success.data) {
  villageLinks = fromHomeLinks(rawVillagesData.success.data.villages)
}

const simpleBannerClick = (event: Event) => {
  event.preventDefault()
  sendHomeSimpleBannerClick({
    eventCallback: () => {
      navigateTo(strapiPageData?.attributes.simple_banner?.button?.url, {
        external: true,
      })
    },
  })
}

const mediaBannerClick = (event: Event) => {
  event.preventDefault()
  sendHomeMediaBannerClick({
    eventCallback: () => {
      navigateTo(strapiPageData?.attributes.media_banner?.button?.url, {
        external: true,
      })
    },
  })
}

const cardsImagesButtonClick = (event: Event, index: number) => {
  event.preventDefault()
  sendHomeFeaturedDestinationsClick({
    index,
    eventCallback: () => {
      navigateTo(featuredDestinations.items[index].url, { external: true })
    },
  })
}

const imageBannerClick = (event: Event) => {
  event.preventDefault()
  sendHomeImageBannerClick({
    eventCallback: () => {
      navigateTo(imageBanner.link, {
        external: true,
      })
    },
  })
}
</script>
